// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Select, useFormContext } from '@metarouter-private/mr-mui';
import { notify } from '@ion/api';

import { CONSENT_MODES } from 'api/consent-modes/modes';
import MODE_OPTIONS from 'app/components/consent-mode-select/ConsentModeSelect/options';

import useConsentCategories from '../shared/consent-category/ConsentCategorySelect/useConsentCategories';
import { applyBulkConsent, SCOPE } from '../bulkApply';
import { BULK_APPLY_COLUMNS } from '../bulkApplyLayout';
import ConfirmAction from '../ConfirmAction';

// Mirror the per-row ConsentModeSelect: the shared options carry {value,text};
// mr-mui's Select wants {value,label}.
const MODE_SELECT_OPTIONS = MODE_OPTIONS.map(({ value, text }) => ({ value, label: text }));

const SCOPE_OPTIONS = [
  { value: SCOPE.onlyUnset, label: 'Apply to all without a category set' },
  { value: SCOPE.all, label: 'Apply to all' },
];

/**
 * The bulk-apply consent control: a single coupled mode + category + scope picker
 * that mass-applies consent across the whole selection via applyBulkConsent +
 * reset (standalone local state, NOT RHF-bound — it reads/writes the form only on
 * confirm). It mirrors the per-row consent rules: External forbids a category, so
 * the category select disables and validity needs no category in External mode.
 */
export default function BulkApplyConsent({ consentCategoryEnabled, consentModeEnabled, onApplied }) {
  const { getValues, reset } = useFormContext();
  const consentCategories = useConsentCategories();

  const [mode, setMode] = useState(CONSENT_MODES.ace);
  const [categoryId, setCategoryId] = useState('');
  const [scope, setScope] = useState(SCOPE.onlyUnset);

  // consentModeEnabled ANDs the same two flags consentCategoryEnabled does (plus the
  // mode flag), so it implies consentCategoryEnabled — whenever this renders, the
  // category control is valid and only the mode control needs its own gate below.
  if (!consentCategoryEnabled && !consentModeEnabled) {
    return null;
  }

  const isExternal = mode === CONSENT_MODES.external;

  // External is always valid (it clears the category); ACE needs a category pick.
  const isValid = isExternal || categoryId !== '';

  // Give the empty (unpicked) state a real option so MUI doesn't warn about a
  // value with no matching MenuItem; disabled so it can't be re-selected.
  const categoryOptions = [{ value: '', label: 'Select a category…', disabled: true }, ...consentCategories];

  const handleApply = () => {
    const values = getValues();
    const next = applyBulkConsent(values, {
      // Only carry mode when the mode feature is on — otherwise the per-row UI
      // never sets it and the form never seeds it, so bulk-apply must leave it
      // untouched rather than emit a mode for a mode-disabled org.
      mode: consentModeEnabled ? mode : undefined,
      categoryId: isExternal ? undefined : categoryId,
      scope,
    });

    reset(next);

    // Count by content, not reference: under scope=all applyBulkConsent reallocates
    // every target object even when the consent fields are unchanged, so an identity
    // check would over-report. Compare the fields that bulk-apply actually touches.
    const count = Object.keys(values).filter(
      pipelineId =>
        values[pipelineId].consentMode !== next[pipelineId].consentMode ||
        (values[pipelineId].consentCategoryId || '') !== (next[pipelineId].consentCategoryId || '')
    ).length;
    notify.success({ message: `Applied consent to ${count} pipeline${count === 1 ? '' : 's'}.` });

    onApplied();
  };

  return (
    <Grid container spacing={2} alignItems="center">
      {consentModeEnabled && (
        <Grid item xs={BULK_APPLY_COLUMNS.primary}>
          <Select
            value={mode}
            onChange={event => setMode(event.target.value)}
            options={MODE_SELECT_OPTIONS}
            label="Consent Mode"
            variant="outlined"
            size="small"
            fullWidth
            data-test-id="bulkConsentModeSelect"
          />
        </Grid>
      )}
      <Grid item xs={BULK_APPLY_COLUMNS.secondary}>
        <Select
          value={categoryId}
          onChange={event => setCategoryId(event.target.value)}
          options={categoryOptions}
          label="Consent Category"
          variant="outlined"
          size="small"
          required={!isExternal}
          disabled={isExternal}
          fullWidth
          data-test-id="bulkConsentCategorySelect"
        />
      </Grid>
      <Grid item xs={BULK_APPLY_COLUMNS.scope}>
        <Select
          value={scope}
          onChange={event => setScope(event.target.value)}
          options={SCOPE_OPTIONS}
          label="Scope"
          variant="outlined"
          size="small"
          fullWidth
          data-test-id="bulkConsentScopeSelect"
        />
      </Grid>
      <Grid item xs={BULK_APPLY_COLUMNS.action}>
        <ConfirmAction onConfirm={handleApply} disabled={!isValid} label="Apply consent" />
      </Grid>
    </Grid>
  );
}

BulkApplyConsent.propTypes = {
  consentCategoryEnabled: PropTypes.bool.isRequired,
  consentModeEnabled: PropTypes.bool.isRequired,
  onApplied: PropTypes.func.isRequired,
};
